import { useEffect } from "react";
import { Cookie } from "../services/storage";
import { COOKIE_AD_ATKN, REDIRECT_URI } from "../constants/storage";
import {
	DISABLED_FEATURES,
	useFeatureToggle,
} from "../utils/feature-toggle/useFeatureToggle";
import { ROUTES } from "../configs/routes";
import { API_BASE_URL } from "../constants/api";
import { TURKEY_SA_COUNTRY_CODE } from "../constants/countryDefaults";
import { useSiteSettingsHook } from "./site-settings-hooks";
import { useAdssoLoginGuard } from "./adsso-login-guard-hooks";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery } from "./query-hooks";
import { ApplicationState } from "../store";
import { UserState } from "../store/reducers/user-reducer";
import { useSelector } from "react-redux";
import { AuthStatus } from "../types/user";
import { useLayout } from "./layout-hooks";
import GoogleAnalytics from "../utils/GoogleAnalytics";
import i18next from "i18next";
import { useUserHooks } from "./user-hooks";
import { useMaintenance } from "./maintenance-page-hooks";
import { useLocale } from "./locale-hooks";
import { IS_LOCALHOST } from "../constants/environment";
import { SystemState } from "../store/reducers/site-reducer";
import { useLoginOptionHooks } from "./login-option-hooks";

export const useLayoutStartup = () => {
	const { pathname } = useLocation();
	const history = useHistory();
	const query = useQuery();
	const { isDisabled } = useFeatureToggle();
	const { loadSystemProperties } = useSiteSettingsHook();
	const { ssoLoginRedirect } = useAdssoLoginGuard();
	const { removeEmailLoginSession } = useLoginOptionHooks();
	const { removeSelectedCountry, getUserCountry, setSelectedCountry } =
		useLayout();
	const { isLoading: isMaintenanceLoading } = useMaintenance();
	const { getUserData, validateSession, loadUserCountryCode } = useUserHooks();
	const { loadLocale } = useLocale();

	//Redux Hooks
	const { isLoading: siteSettingIsLoading } = useSelector<
		ApplicationState,
		SystemState
	>((state) => state.system);
	const { userData, userCountryCode, isAuthenticated } = useSelector<
		ApplicationState,
		UserState
	>((state) => state.user);

	const redirectUri = Cookie.get(REDIRECT_URI);
	// hasPageAccess should only check i18n initialization and loading state
	// maintenance routing is handled by Router.tsx route guard
	const hasPageAccess = i18next.isInitialized && !isMaintenanceLoading;

	const loadSiteSettings = () => {
		if (!isDisabled(DISABLED_FEATURES.SITE_SETTINGS)) loadSystemProperties();
	};

	const redirectIfNoADSSOToken = () => {
		if (
			!isDisabled(DISABLED_FEATURES.ADSSO_SITE_RESTRICTION) &&
			!Cookie.get(COOKIE_AD_ATKN)
		) {
			ssoLoginRedirect();
		}
	};

	const handleLoginDesignatedPageRedirect = () => {
		// Only redirect after session validation confirms authentication
		if (isAuthenticated !== AuthStatus.Authenticated) return;
		//Redirect to designated page after logging in using SA Account
		if (redirectUri && query.get("success")) {
			globalThis.location.replace(`${API_BASE_URL}${redirectUri}`);
			Cookie.remove(REDIRECT_URI);
		}
	};

	const handleOldGRPSRedirect = () => {
		if (pathname === ROUTES.OLD_ABOUT_GRPS) {
			history.replace(ROUTES.ABOUT_GRPS);
		}
	};

	const handleRemoveTurkeySAUserCC = () => {
		if (
			userData?.countryCode === TURKEY_SA_COUNTRY_CODE &&
			isAuthenticated === AuthStatus.Authenticated
		) {
			removeSelectedCountry();
		}
	};

	useEffect(() => {
		// Wait for maintenance config to load before making any API calls
		if (isMaintenanceLoading) return;
		// Load locale immediately after maintenance config is loaded
		// This ensures i18next has the correct language set before any page renders (including MaintenancePage)
		loadLocale();
		// Redirect page to ADSSO verification page when token is not yet generated
		redirectIfNoADSSOToken();
		// Load site-settings
		loadSiteSettings();
		// Handle proper page redirects when user logged in using SA Account
		handleLoginDesignatedPageRedirect();
		// Handle user redirection to new GRPS Page when user access old link.
		handleOldGRPSRedirect();
		// Handle removal of user selected country code when user logged in with TR SA-Countrycode
		handleRemoveTurkeySAUserCC();
	}, [isMaintenanceLoading, isAuthenticated]);

	useEffect(() => {
		// Dont initialize GA on localhost for development purposes.
		if (IS_LOCALHOST) return;

		// Send page views to Google Analytics
		GoogleAnalytics.initialize(globalThis.location.host);
		if (GoogleAnalytics.isInitialized) GoogleAnalytics.sendPageView(pathname);
	}, [pathname]);

	// Handle loading of locales and redirection of user to china local site when country code is CN
	useEffect(() => {
		if (!userCountryCode) return;
		loadLocale();

		const country = getUserCountry();
		setSelectedCountry(country);
	}, [userCountryCode]);

	// getUserCountryCode and getUserData (profile API) when user loggeds in.
	useEffect(() => {
		// Wait for both site settings and maintenance config to load
		if (siteSettingIsLoading || isMaintenanceLoading) return;

		loadUserCountryCode();
		removeEmailLoginSession(); //cleanup
		// Always validate session via Profile API — replaces UtilsUser.isUserLoggedIn() cookie check
		validateSession();
	}, [siteSettingIsLoading, isMaintenanceLoading]);

	return { hasPageAccess };
};
